<script setup lang="ts">
import { useIntersectionAnimation } from "@/composables/use-intersection-animation";
import PublicJobCard from "@/components/marketing/jobs/PublicJobCard.vue";
import NoJobsCallToAction from "@/components/marketing/jobs/NoJobsCallToAction.vue";
import type { JobWithMetaDescription, SelectItem } from "@/types";
import type { Pagination } from "@/types/marketing";

interface Props {
  jobs: JobWithMetaDescription[];
  pagination: Pagination;
  pending: boolean;
  parentSource: string;
  searchQuery: string;
  selectedLocation?: { formattedAddress: string };
  selectedCapacity?: SelectItem;
  selectedExperience?: SelectItem;
  selectedWorkingModel?: SelectItem;
  currentPage: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  goToPage: [page: number];
}>();

const { elementRef } = useIntersectionAnimation();

const goToPage = (page: number) => {
  emit("goToPage", page);
};

// When jobs change (e.g., after filtering), ensure new fade-up elements become visible
// The intersection observer only fires once, so we need to manually trigger visibility for dynamic content
watch(
  () => props.jobs,
  () => {
    nextTick(() => {
      if (elementRef.value) {
        const fadeElements = elementRef.value.querySelectorAll(".fade-up:not(.visible)");
        fadeElements.forEach((el, index) => {
          setTimeout(() => {
            el.classList.add("visible");
          }, index * 50);
        });
      }
    });
  },
  { flush: "post" }
);

// Generate visible page numbers
const visiblePages = computed(() => {
  const totalPages = props.pagination.totalPages;
  const current = props.currentPage;
  const pages: (number | string)[] = [];

  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);

    if (current > 3) {
      pages.push("...");
    }

    const start = Math.max(2, current - 1);
    const end = Math.min(totalPages - 1, current + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (current < totalPages - 2) {
      pages.push("...");
    }

    pages.push(totalPages);
  }

  return pages;
});
</script>

<template>
  <section ref="elementRef" class="relative py-8 lg:py-12 2xl:py-16 3xl:py-20 4xl:py-24 bg-warm-white">
    <div
      class="max-w-[1400px] 2xl:max-w-[1600px] 3xl:max-w-[1800px] 4xl:max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12 3xl:px-16 4xl:px-20"
    >
      <!-- Results Count -->
      <div v-if="pending || jobs.length > 0" class="mb-6 2xl:mb-8 fade-up">
        <div class="flex items-center justify-between">
          <p v-if="!pending" class="text-lg 2xl:text-xl 3xl:text-2xl 4xl:text-3xl text-dark-gray">
            <span class="font-secondary font-bold text-gradient-warm">{{ pagination.total }}</span>
            <span class="font-medium"> job{{ pagination.total === 1 ? "" : "s" }} found</span>
          </p>
          <p v-else class="text-lg 2xl:text-xl 3xl:text-2xl 4xl:text-3xl text-dark-gray">
            <span class="inline-flex items-center gap-2">
              <Icon name="svg-spinners:3-dots-scale" class="w-6 h-6 2xl:w-7 2xl:h-7 text-primary" />
              <span class="font-medium">Finding opportunities...</span>
            </span>
          </p>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="pending" class="space-y-4 2xl:space-y-6">
        <div v-for="i in 5" :key="i" class="animate-pulse">
          <div class="bg-white rounded-2xl 2xl:rounded-3xl border border-sand/50 p-6 2xl:p-8 shadow-warm">
            <div class="flex items-start gap-4 2xl:gap-6">
              <div class="w-16 h-16 2xl:w-20 2xl:h-20 bg-sand/50 rounded-xl 2xl:rounded-2xl flex-shrink-0"></div>
              <div class="flex-1 min-w-0">
                <div class="h-4 2xl:h-5 bg-sand/50 rounded w-1/4 mb-2"></div>
                <div class="h-6 2xl:h-7 bg-sand/50 rounded w-3/4 mb-3"></div>
                <div class="h-4 2xl:h-5 bg-sand/50 rounded w-full mb-3"></div>
                <!-- Tags row placeholder -->
                <div class="flex gap-2 mb-4">
                  <div class="h-6 2xl:h-7 bg-sand/50 rounded-full w-16"></div>
                  <div class="h-6 2xl:h-7 bg-sand/50 rounded-full w-20"></div>
                  <div class="h-6 2xl:h-7 bg-sand/50 rounded-full w-14"></div>
                  <div class="h-6 2xl:h-7 bg-sand/50 rounded-full w-18"></div>
                </div>
                <!-- Footer row with salary and CTA -->
                <div class="flex items-center justify-between pt-3 border-t border-sand/30">
                  <div class="h-7 2xl:h-8 bg-sand/50 rounded-lg w-24"></div>
                  <div class="h-10 2xl:h-12 bg-sand/50 rounded-xl w-28"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Results -->
      <NoJobsCallToAction
        v-else-if="!pending && jobs.length === 0"
        :search-query="searchQuery"
        :location="selectedLocation?.formattedAddress"
        :capacity="selectedCapacity"
        :experience="selectedExperience"
        :working-model="selectedWorkingModel"
        class="fade-up"
      />

      <!-- Job Cards -->
      <div v-else-if="!pending && jobs.length > 0" class="space-y-4 2xl:space-y-6">
        <div v-for="(job, index) in jobs" :key="job.id" class="fade-up" :style="{ '--animation-delay': `${index * 50}ms` }">
          <PublicJobCard
            :job="job"
            :parent-source="parentSource"
            class="!rounded-2xl 2xl:!rounded-3xl !border-sand/50 hover:!border-primary/30 !shadow-warm hover:!shadow-warm-lg transition-all duration-300"
          />
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.totalPages > 1" class="mt-12 2xl:mt-16 flex justify-center fade-up" style="--animation-delay: 300ms">
        <nav class="flex items-center gap-2 2xl:gap-3" aria-label="Pagination">
          <!-- Previous -->
          <button
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1"
            class="flex items-center gap-1 px-4 py-2 2xl:px-5 2xl:py-3 text-sm 2xl:text-base font-medium text-medium-gray bg-white border border-sand rounded-xl 2xl:rounded-2xl hover:bg-warm-white hover:border-primary/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            <Icon name="material-symbols:chevron-left" class="w-5 h-5" />
            <span class="hidden sm:inline">Previous</span>
          </button>

          <!-- Page Numbers -->
          <div class="flex items-center gap-1 2xl:gap-2">
            <template v-for="page in visiblePages" :key="page">
              <span v-if="page === '...'" class="px-2 py-2 text-medium-gray"> ... </span>
              <button
                v-else
                @click="goToPage(page as number)"
                :class="[
                  'w-10 h-10 2xl:w-12 2xl:h-12 text-sm 2xl:text-base font-medium rounded-xl 2xl:rounded-2xl transition-all duration-200',
                  page === currentPage
                    ? 'bg-gradient-to-br from-primary to-primary-light text-white shadow-warm'
                    : 'text-medium-gray bg-white border border-sand hover:bg-warm-white hover:border-primary/30',
                ]"
              >
                {{ page }}
              </button>
            </template>
          </div>

          <!-- Next -->
          <button
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage === pagination.totalPages"
            class="flex items-center gap-1 px-4 py-2 2xl:px-5 2xl:py-3 text-sm 2xl:text-base font-medium text-medium-gray bg-white border border-sand rounded-xl 2xl:rounded-2xl hover:bg-warm-white hover:border-primary/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            <span class="hidden sm:inline">Next</span>
            <Icon name="material-symbols:chevron-right" class="w-5 h-5" />
          </button>
        </nav>
      </div>
    </div>
  </section>
</template>
